import { datadogRum } from '@datadog/browser-rum';

import { isStaleChunkError, STALE_CHUNK_MESSAGE_PATTERNS } from './utilities/loadableRetry';

export const BANNER_ID = 'chunk-load-refresh-banner';

export const REFRESH_PROMPT_MESSAGE = 'A new version is available. Please refresh to continue.';

// Surfaced so the ErrorBoundary fallback reports through the same action as the banner —
// members and experts both emit this, so partners becomes comparable in RUM.
export const emitRefreshPrompted = (surface: 'banner' | 'error-boundary') => {
    // RUM off-prod raises "no credentials" — downgrade to warn; other failures must not tear down the UI.
    try {
        datadogRum.addAction('chunk-load-refresh-prompted', {
            path: window.location.pathname,
            surface,
        });
    } catch (error) {
        if (error instanceof Error && error.message === 'No credentials, applicationId or region') {
            console.warn(error);
        } else {
            console.error('[chunkLoadRecovery] Failed to emit RUM action', error);
        }
    }
};

const showRefreshBanner = () => {
    if (document.getElementById(BANNER_ID)) return;

    const banner = document.createElement('div');
    banner.id = BANNER_ID;
    banner.setAttribute('role', 'status');
    banner.style.cssText = [
        'position:fixed',
        'left:50%',
        'bottom:24px',
        'transform:translateX(-50%)',
        'z-index:2147483647',
        'display:flex',
        'align-items:center',
        'gap:16px',
        'padding:14px 20px',
        'background:#0066AC',
        'color:#fff',
        'border-radius:10px',
        'box-shadow:0 8px 24px rgba(0,0,0,0.28)',
        'font:600 14px/1.4 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
    ].join(';');

    const message = document.createElement('span');
    message.textContent = REFRESH_PROMPT_MESSAGE;

    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = 'Refresh';
    button.style.cssText = [
        'min-width:44px',
        'min-height:44px',
        'padding:0 20px',
        'background:#fff',
        'color:#0066AC',
        'border:0',
        'border-radius:6px',
        'font:700 14px/1 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
        'cursor:pointer',
    ].join(';');
    button.addEventListener('click', () => window.location.reload());

    banner.appendChild(message);
    banner.appendChild(button);
    document.body.appendChild(banner);

    emitRefreshPrompted('banner');
};

const handlePreloadError = (event: Event) => {
    event.preventDefault();
    showRefreshBanner();
};

// Some browsers only populate event.message (not event.error), so fall back to a raw pattern test.
const handleWindowError = (event: ErrorEvent) => {
    if (isStaleChunkError(event.error)) {
        event.preventDefault();
        showRefreshBanner();
        return;
    }
    const message = event.message ?? '';
    if (STALE_CHUNK_MESSAGE_PATTERNS.some((p) => p.test(message))) {
        event.preventDefault();
        showRefreshBanner();
    }
};

let registered = false;

// Prompts a refresh on stale-chunk failures instead of falling through to the AppCrash boundary.
//
// Note this only covers the failures that reach the window: Vite's preload error, and async
// chunk errors thrown outside React's render. Because partners uses React.lazy rather than
// @loadable/component, a rejected import() is re-thrown during render and caught by the
// ErrorBoundary instead — CrashFallback handles that half. Both are needed.
export const registerChunkLoadRecovery = (): (() => void) => {
    if (registered) return () => undefined;
    // Capture phase, matching apps/experts. Both listeners must add and remove with the same
    // flag or removeEventListener silently no-ops and cleanup leaks the handler.
    window.addEventListener('vite:preloadError', handlePreloadError, true);
    window.addEventListener('error', handleWindowError, true);
    registered = true;
    const cleanup = () => {
        window.removeEventListener('vite:preloadError', handlePreloadError, true);
        window.removeEventListener('error', handleWindowError, true);
        registered = false;
    };
    // HMR replaces this module without re-running index.tsx registration — dispose prevents a duplicate listener.
    if (import.meta.hot) {
        import.meta.hot.dispose(cleanup);
    }
    return cleanup;
};
